import { useState, useEffect, useCallback, useRef } from 'react';
import {
  publicCompanyService,
  PublicCompanyItem,
  PublicCompaniesResponse,
} from '../services/publicCompanyService';

export interface UsePublicCompaniesReturn {
  companies: PublicCompanyItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// ─── モジュールレベルの in-memory cache ───────────────────────────
// 目的:
//  - ページ遷移 (例: /companies → /companies/N/profile → 戻る) ごとに
//    /api/public/companies が再呼び出しされるのを防ぐ
//  - React 18 StrictMode の useEffect 二重実行を共有 Promise で1回に集約
//  - 同一 limit に対する concurrent 呼び出しもまとめる
//
// TTL: 5分。短すぎると効果薄、長すぎると新規企業の反映が遅れる。
const CACHE_TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
  data: PublicCompanyItem[];
  ts: number;
  promise?: Promise<PublicCompaniesResponse>;
}

const cache = new Map<number, CacheEntry>();

// limit が undefined の場合のキー (デフォルト=12 として扱う)
const cacheKey = (limit?: number): number =>
  typeof limit === 'number' && Number.isFinite(limit) && limit > 0
    ? limit
    : 12;

const isFresh = (entry: CacheEntry): boolean =>
  Date.now() - entry.ts < CACHE_TTL_MS;

/**
 * キャッシュからデータを取得する。新鮮なキャッシュがあれば即返却、
 * 進行中の Promise があればそれを await、なければ新規 fetch。
 */
const fetchWithCache = async (
  limit: number | undefined,
  force = false
): Promise<PublicCompanyItem[]> => {
  const key = cacheKey(limit);
  const existing = cache.get(key);

  if (!force && existing && isFresh(existing) && !existing.promise) {
    return existing.data;
  }

  if (!force && existing?.promise) {
    const res = await existing.promise;
    return res.data.companies;
  }

  const promise = publicCompanyService.getPublicCompanies(limit);
  cache.set(key, {
    data: existing?.data ?? [],
    ts: existing?.ts ?? 0,
    promise,
  });

  try {
    const res = await promise;
    cache.set(key, {
      data: res.data.companies,
      ts: Date.now(),
    });
    return res.data.companies;
  } catch (err) {
    if (existing) {
      cache.set(key, { data: existing.data, ts: existing.ts });
    } else {
      cache.delete(key);
    }
    throw err;
  }
};

export const usePublicCompanies = (
  limit?: number
): UsePublicCompaniesReturn => {
  const key = cacheKey(limit);
  const cached = cache.get(key);
  const hasFreshCache = !!cached && isFresh(cached) && !cached.promise;

  const [companies, setCompanies] = useState<PublicCompanyItem[]>(
    hasFreshCache ? cached!.data : []
  );
  const [loading, setLoading] = useState<boolean>(!hasFreshCache);
  const [error, setError] = useState<string | null>(null);

  // unmount 後の setState を防ぐ
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(
    async (force = false) => {
      const currentCached = cache.get(cacheKey(limit));
      const cacheValid =
        !force &&
        !!currentCached &&
        isFresh(currentCached) &&
        !currentCached.promise;

      if (cacheValid) {
        if (mountedRef.current) {
          setCompanies(currentCached!.data);
          setLoading(false);
          setError(null);
        }
        return;
      }

      if (mountedRef.current) {
        setLoading(true);
        setError(null);
      }
      try {
        const data = await fetchWithCache(limit, force);
        if (mountedRef.current) {
          setCompanies(data);
        }
      } catch (err) {
        if (mountedRef.current) {
          setError('企業一覧の取得に失敗しました');
        }
        console.error('公開企業一覧取得エラー:', err);
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    },
    [limit]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const refetch = useCallback(() => load(true), [load]);

  return { companies, loading, error, refetch };
};

/** @internal テスト用: モジュールキャッシュをクリア */
export const __resetPublicCompaniesCacheForTests = (): void => {
  cache.clear();
};
